import {
  createStyles,
  Grid,
  LinearProgress,
  Link,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import { useGetInternalTaskQuery, useUpdateInternalTaskMutation } from "../../../api/rtk/InternalTaskApi";
import { SwissGermanLocale, SwissTimeZone } from "../../../data/constants";
import { InternalTaskViewModel } from "../../../model/InternalTaskViewModel";
import { TaskState } from "../../../model/TaskState";
import { routesPaths } from "../../../Routes";
import { GetTranslation } from "../../../translations/GetTranslation";
import { navigateBack } from "../../../utils/Navigation";
import CustomButton from "../../common/buttons/CustomButton";
import DefaultButtons from "../../common/buttons/DefaultButtons";
import TranslatedMessage from "../../translations/TranslatedMessage";
import CandidateFields from "./CandidateFields";
import ModuleFields from "./ModuleFields";
import { ModuleViewModel } from "../../../model/Module";
import { getModule } from "../../../api/ModuleApi";

const useStyles = makeStyles((theme) =>
  createStyles({
    section: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(3),
    },
    checkbox: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    row: {
      display: "flex",
      alignItems: "center",
    },
  })
);

const CancellationForm: FC = () => {
  const history = useHistory();
  const { internalTaskId } = useParams<{ internalTaskId: string }>();
  const intl = useIntl();
  const classes = useStyles();

  const [internalTask, setInternalTask] = useState<InternalTaskViewModel>();
  const { data: fetchedInternalTask, isFetching: isInternalTaskFetching } = useGetInternalTaskQuery(internalTaskId);
  const [updateInternalTask, { isLoading: isUpdateInternalTaskLoading }] = useUpdateInternalTaskMutation();
  const isLoading = isInternalTaskFetching || isUpdateInternalTaskLoading;
  const [module, setModule] = useState<ModuleViewModel | null>(null);

  useEffect(() => {
    if (fetchedInternalTask !== undefined) {
      setInternalTask(fetchedInternalTask);
      getModule(fetchedInternalTask.moduleId).then((response) => {
        setModule(response);
      });
    }
  }, [fetchedInternalTask]);

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if (fetchedInternalTask === undefined || internalTask === undefined) {
    return <></>;
  }

  const close = () => navigateBack(routesPaths.root(), history, history.location);

  const handleCommentsChange = (event: ChangeEvent<HTMLInputElement>) =>
    setInternalTask({ ...internalTask, comments: event.target.value });

  const handleSave = () => {
    const requestData = { ...internalTask, state: TaskState.InProgress };
    updateInternalTask(requestData);
  };

  const handleClose = () => close();

  const handleComplete = async () => {
    const requestData = { ...internalTask, state: TaskState.Done,  candidateWillGetRefund: true};
    const response = await updateInternalTask(requestData).unwrap();
    if (response.success) {
      close();
    }
  };

  const creationDateDisplay = `${GetTranslation(intl, "cancellationTaskForm.createdOn")} ${new Date(
    internalTask.creationDate
  ).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone })}`;

  const completeTaskButton = (
    <CustomButton
      tooltip={<TranslatedMessage id="cancellationTaskForm.completeTask" />}
      onClick={handleComplete}
    >
      <AssignmentTurnedInIcon />
    </CustomButton>
  );

  return (
    <Grid container item direction="column" xs={12} sm={12} md={12} lg={10} xl={8}>
      <Grid container item direction="row">
        <Grid container item direction="column" xs={12} sm={6}>
          <Typography variant="h5">
            <TranslatedMessage id="cancellationTaskForm.header" />
          </Typography>
          <Typography variant="body2">{creationDateDisplay}</Typography>
        </Grid>

        <Grid container item direction="row" justify="flex-end" xs={12} sm={6}>
          <DefaultButtons
            isDeleteButtonVisible={false}
            additionalButtons={completeTaskButton}
            onSave={handleSave}
            onClose={handleClose}
          />
        </Grid>
      </Grid>

      <Paper className={classes.section} elevation={2}>
        <Grid container direction="row">
          <CandidateFields task={internalTask} module={module} />
          <ModuleFields task={internalTask} />
        </Grid>
        {internalTask.examSessionCandidateId && internalTask.moduleId && (
          <Link
            component={RouterLink}
            to={routesPaths.examSessionCandidate(internalTask.examSessionCandidateId, internalTask.moduleId)}
          >
            <TranslatedMessage id="cancellationTaskForm.navigateToCandidateSession" />
          </Link>
        )}
      </Paper>

      <Paper className={classes.section} elevation={2}>
        <Grid container item direction="column" spacing={2} xs={12} sm={10} md={8}>
          <Grid item container direction="row" alignItems="baseline">
            <TextField
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              label={GetTranslation(intl, "cancellationTaskForm.commentsLabel")}
              value={internalTask.comments}
              onChange={handleCommentsChange}
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default CancellationForm;
